import React, { useCallback, useEffect, useState } from 'react';
import {
  downloadFanoutSloBreachReportCsv,
  fetchFanoutSloBreachReport,
  type FanoutSloBreachReportParams,
  type FanoutSloBreachReportResponse,
} from '../api/reports';
import {
  collapseEmptyHistory,
  fanoutDailyHasActivity,
} from '../components/reports/collapseEmptyHistory';
import { ReportGeneratedHeader } from '../components/reports/ReportGeneratedHeader';
import { ShowEmptyDaysToggle } from '../components/reports/ShowEmptyDaysToggle';
import { borderRadius, colors, spacing, typography, typographyFlat } from '../styles/tokens';
import { formatInstant } from '../utils/datetime';

export const ReportsFanoutSloBreachesPage: React.FC = () => {
  const [report, setReport] = useState<FanoutSloBreachReportResponse | null>(null);
  const [days, setDays] = useState('14');
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showEmptyDays, setShowEmptyDays] = useState(false);

  const buildParams = useCallback((): FanoutSloBreachReportParams => {
    const parsedDays = Number.parseInt(days, 10);
    return {
      days: Number.isNaN(parsedDays) || parsedDays < 7 ? 14 : parsedDays,
    };
  }, [days]);

  const loadReport = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const nextReport = await fetchFanoutSloBreachReport(buildParams());
      setReport(nextReport);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load fanout SLO breach report');
      setReport(null);
    } finally {
      setIsLoading(false);
    }
  }, [buildParams]);

  useEffect(() => {
    void loadReport();
  }, []);

  const handleApplyFilters = async (event: React.FormEvent) => {
    event.preventDefault();
    await loadReport();
  };

  const handleExportCsv = async () => {
    try {
      setIsExporting(true);
      await downloadFanoutSloBreachReportCsv(buildParams());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to export fanout SLO breach report');
    } finally {
      setIsExporting(false);
    }
  };

  const dailySummary = report
    ? collapseEmptyHistory(report.daily_summary, {
        periodKey: (item) => item.date,
        hasActivity: fanoutDailyHasActivity,
        showEmptyDays,
        generatedAt: report.generated_at,
      })
    : [];

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Reports: Fanout SLO Breaches</h1>
        <p style={styles.subtitle}>
          Historical breach windows for reservation latency, outbox handoff lag, retry exhaustion, and oversell incidents with recovery timing.
        </p>
      </div>

      <ReportGeneratedHeader
        generatedAt={report?.generated_at}
        onRefresh={() => {
          void loadReport();
        }}
        refreshing={isLoading}
      />

      <form style={styles.filtersRow} onSubmit={handleApplyFilters}>
        <label style={styles.filterLabel}>
          Window (days)
          <input type="number" min={7} max={90} value={days} onChange={(event) => setDays(event.target.value)} style={styles.input} />
        </label>

        <div style={styles.actions}>
          <button type="submit" style={styles.primaryButton} disabled={isLoading}>
            {isLoading ? 'Loading...' : 'Apply'}
          </button>
          <button type="button" style={styles.secondaryButton} onClick={handleExportCsv} disabled={isLoading || isExporting}>
            {isExporting ? 'Exporting...' : 'Export CSV'}
          </button>
        </div>
      </form>

      {error && <div style={styles.error}>{error}</div>}

      {report && (
        <>
          <div style={styles.summaryGrid}>
            <MetricCard label="Breach Windows" value={String(report.summary.total_breach_windows)} />
            <MetricCard label="Warning" value={String(report.summary.severity_counts.warning ?? 0)} />
            <MetricCard label="High" value={String(report.summary.severity_counts.high ?? 0)} />
            <MetricCard label="Critical" value={String(report.summary.severity_counts.critical ?? 0)} />
          </div>

          <section style={styles.card}>
            <div style={styles.sectionHeader}>
              <h2 style={styles.sectionTitle}>Daily Summary</h2>
              <ShowEmptyDaysToggle checked={showEmptyDays} onChange={setShowEmptyDays} />
            </div>
            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Date</th>
                    <th style={styles.th}>Breach Windows</th>
                    <th style={styles.th}>Severity Counts</th>
                    <th style={styles.th}>Metric Counts</th>
                    <th style={styles.th}>Retry Exhaustion Delta</th>
                    <th style={styles.th}>Oversell Delta</th>
                  </tr>
                </thead>
                <tbody>
                  {dailySummary.length === 0 && (
                    <tr>
                      <td colSpan={6} style={styles.emptyCell}>
                        {report.daily_summary.length === 0
                          ? 'No daily summary rows are available.'
                          : 'No activity in this window.'}
                      </td>
                    </tr>
                  )}
                  {dailySummary.map((item) => (
                    <tr key={item.date}>
                      <td style={styles.td}>{item.date}</td>
                      <td style={styles.td}>{item.total_breach_windows}</td>
                      <td style={styles.td}>{formatCounts(item.severity_counts)}</td>
                      <td style={styles.td}>{formatCounts(item.metric_counts)}</td>
                      <td style={styles.td}>{item.counter_deltas.retry_exhaustion_total}</td>
                      <td style={styles.td}>{item.counter_deltas.oversell_exception_total}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section style={styles.card}>
            <div style={styles.sectionHeader}>
              <h2 style={styles.sectionTitle}>Breach Windows</h2>
            </div>
            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Metric</th>
                    <th style={styles.th}>Severity</th>
                    <th style={styles.th}>Threshold</th>
                    <th style={styles.th}>Start</th>
                    <th style={styles.th}>End</th>
                    <th style={styles.th}>Recovered</th>
                    <th style={styles.th}>Duration</th>
                    <th style={styles.th}>Peak</th>
                    <th style={styles.th}>Samples</th>
                  </tr>
                </thead>
                <tbody>
                  {report.breach_windows.length === 0 && (
                    <tr>
                      <td colSpan={9} style={styles.emptyCell}>No breach windows were detected for the selected window.</td>
                    </tr>
                  )}
                  {report.breach_windows.map((item) => (
                    <tr key={`${item.metric_name}-${item.breach_start}`}>
                      <td style={styles.tdStrong}>{formatMetricName(item.metric_name)}</td>
                      <td style={styles.td}>{item.severity}</td>
                      <td style={styles.td}>{item.threshold_type} {item.threshold_value}</td>
                      <td style={styles.td}>{formatTimestamp(item.breach_start)}</td>
                      <td style={styles.td}>{formatTimestamp(item.breach_end)}</td>
                      <td style={styles.td}>{formatTimestamp(item.recovered_at)}</td>
                      <td style={styles.td}>{formatDuration(item.duration_seconds)}</td>
                      <td style={styles.td}>{item.max_observed_value}</td>
                      <td style={styles.td}>{item.sample_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </div>
  );
};

function MetricCard({ label, value }: { label: string; value: string }) {
  return (
    <div style={styles.metricCard}>
      <span style={styles.metricLabel}>{label}</span>
      <strong style={styles.metricValue}>{value}</strong>
    </div>
  );
}

function formatCounts(values: Record<string, number>): string {
  const entries = Object.entries(values);
  if (entries.length === 0) {
    return '-';
  }
  return entries.map(([key, value]) => `${key}: ${value}`).join(' | ');
}

function formatMetricName(value: string): string {
  return value.split('_').join(' ');
}

function formatTimestamp(value: string | null): string {
  return formatInstant(value, '-');
}

function formatDuration(value: number): string {
  const seconds = Math.max(0, Math.floor(value));
  if (seconds < 60) {
    return `${seconds}s`;
  }
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  if (minutes < 60) {
    return `${minutes}m ${remainingSeconds}s`;
  }
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours}h ${remainingMinutes}m`;
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: spacing.lg,
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.lg,
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.xs,
  },
  title: {
    margin: 0,
    fontSize: typography.h1.fontSize,
    fontWeight: typography.h1.fontWeight,
    color: colors.text,
  },
  subtitle: {
    margin: 0,
    color: colors.textSecondary,
    fontSize: typography.body.fontSize,
  },
  filtersRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: spacing.md,
    alignItems: 'end',
    padding: spacing.md,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    backgroundColor: colors.surface,
  },
  filterLabel: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.xs,
    color: colors.text,
    fontSize: typography.small.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
    minWidth: '150px',
  },
  input: {
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.sm,
    padding: `${spacing.sm} ${spacing.md}`,
    fontSize: typography.body.fontSize,
    color: colors.text,
    backgroundColor: colors.background,
  },
  actions: {
    display: 'flex',
    gap: spacing.sm,
  },
  primaryButton: {
    border: 'none',
    borderRadius: borderRadius.sm,
    backgroundColor: colors.primary,
    color: colors.white,
    padding: `${spacing.sm} ${spacing.md}`,
    cursor: 'pointer',
    fontSize: typography.body.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
  },
  secondaryButton: {
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.sm,
    backgroundColor: colors.surface,
    color: colors.text,
    padding: `${spacing.sm} ${spacing.md}`,
    cursor: 'pointer',
    fontSize: typography.body.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
  },
  error: {
    padding: spacing.md,
    borderRadius: borderRadius.sm,
    border: `1px solid ${colors.error}`,
    backgroundColor: `${colors.error}20`,
    color: colors.error,
  },
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: spacing.md,
  },
  metricCard: {
    padding: spacing.md,
    borderRadius: borderRadius.md,
    border: `1px solid ${colors.border}`,
    backgroundColor: colors.surface,
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.xs,
  },
  metricLabel: {
    color: colors.textSecondary,
    fontSize: typography.small.fontSize,
  },
  metricValue: {
    color: colors.text,
    fontSize: typography.h2.fontSize,
    fontWeight: typography.h2.fontWeight,
  },
  card: {
    padding: spacing.md,
    borderRadius: borderRadius.md,
    border: `1px solid ${colors.border}`,
    backgroundColor: colors.surface,
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: spacing.sm,
    alignItems: 'center',
    flexWrap: 'wrap',
    marginBottom: spacing.sm,
  },
  sectionTitle: {
    margin: 0,
    color: colors.text,
    fontSize: typography.h3.fontSize,
    fontWeight: typography.h3.fontWeight,
  },
  tableWrap: {
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    textAlign: 'left',
    padding: `${spacing.sm} ${spacing.md}`,
    borderBottom: `1px solid ${colors.border}`,
    color: colors.textSecondary,
    fontSize: typography.small.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
    whiteSpace: 'nowrap',
  },
  td: {
    padding: `${spacing.sm} ${spacing.md}`,
    borderBottom: `1px solid ${colors.border}`,
    color: colors.text,
    fontSize: typography.body.fontSize,
  },
  tdStrong: {
    padding: `${spacing.sm} ${spacing.md}`,
    borderBottom: `1px solid ${colors.border}`,
    color: colors.text,
    fontSize: typography.body.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
  },
  emptyCell: {
    padding: spacing.lg,
    textAlign: 'center',
    color: colors.textSecondary,
  },
};
